// Mortgage-specific subtype data for service_records where
// service_family='loan'. Covers both Traditional and Private Money.

import type { AuditedRow, ISODate, SqliteBoolean, Timestamp, UUID } from "./common";

export type LoanType = "traditional" | "private_money";

export type LoanProduct =
  | "purchase"
  | "refinance"
  | "cash_out_refi"
  | "heloc"
  | "heloan"
  | "reverse"
  | "bridge"
  | "construction"
  | "fix_and_flip"
  | "rental"
  | "commercial"
  | "other";

export type RateType = "fixed" | "arm" | "variable" | "interest_only";

/**
 * Where a loan's interest_rate came from (migration 20260911060137).
 * 'market_estimate' is the only one that is not a recorded note rate; it
 * always travels with interest_rate_is_estimate = 1.
 */
export type InterestRateSource =
  | "closed_loan_report"
  | "activecampaign"
  | "market_estimate"
  | "attom"
  | "manual";

// Canonical loan_program_type slugs (migration 0089 column; the slug set
// normalizeLoanProgramType has always emitted). Grant's ruling 2026-07-12:
// the picker must offer more than Conventional/FHA/VA/USDA — "sometimes we
// get HELOC or Reverse or Non QM or Other".
export type LoanProgramType =
  | "conventional"
  | "fha"
  | "va"
  | "usda"
  | "jumbo"
  | "heloc"
  | "heloan"
  | "reverse"
  | "non_qm"
  | "private_money"
  | "other";

// The option set a human picks from when classifying a TRADITIONAL loan
// (the mortgage-loan-program-type-missing decide rail + any loan editor).
// private_money is deliberately absent — PM is a loan_type, and PM rows
// get loan_program_type='private_money' mechanically at import, never by
// a human classifying a Traditional loan.
export const TRADITIONAL_LOAN_PROGRAM_TYPE_OPTIONS: ReadonlyArray<{
  value: Exclude<LoanProgramType, "private_money">;
  label: string;
}> = [
  { value: "conventional", label: "Conventional" },
  { value: "fha", label: "FHA" },
  { value: "va", label: "VA" },
  { value: "usda", label: "USDA" },
  { value: "jumbo", label: "Jumbo" },
  { value: "heloc", label: "HELOC" },
  { value: "heloan", label: "HELOAN" },
  { value: "reverse", label: "Reverse" },
  { value: "non_qm", label: "Non-QM" },
  { value: "other", label: "Other" },
];

export function isTraditionalLoanProgramType(
  v: string,
): v is Exclude<LoanProgramType, "private_money"> {
  return TRADITIONAL_LOAN_PROGRAM_TYPE_OPTIONS.some((o) => o.value === v);
}

// Loan purpose options (Baker's field-visibility ask, 2026-08-12: the
// Loan page didn't show "Purchase, Refinance" alongside the other loan
// facts, even though loan_product has carried this since migration 0006).
export const LOAN_PRODUCT_OPTIONS: ReadonlyArray<{ value: LoanProduct; label: string }> = [
  { value: "purchase", label: "Purchase" },
  { value: "refinance", label: "Refinance" },
  { value: "cash_out_refi", label: "Cash-Out Refinance" },
  { value: "heloc", label: "HELOC" },
  { value: "heloan", label: "HELOAN" },
  { value: "reverse", label: "Reverse" },
  { value: "bridge", label: "Bridge" },
  { value: "construction", label: "Construction" },
  { value: "fix_and_flip", label: "Fix and Flip" },
  { value: "rental", label: "Rental" },
  { value: "commercial", label: "Commercial" },
  { value: "other", label: "Other" },
];

export function isLoanProduct(v: string): v is LoanProduct {
  return LOAN_PRODUCT_OPTIONS.some((o) => o.value === v);
}

export type PropertyType = "sfr" | "condo" | "townhome" | "multi_family" | "commercial" | "land";

export type PropertyUse = "primary" | "second_home" | "investment";

// Mike's Private Money grading. Orthogonal to Fit Assessment.
export type BorrowerTier =
  | "a_plus"
  | "a"
  | "a_minus"
  | "b_plus"
  | "b"
  | "b_minus"
  | "c_plus"
  | "c"
  | "c_minus"
  | "d"
  | "other";

// ── NMLS Mortgage Call Report classifications (migration 0249) ──────
// Grant, 2026-08-11: the quarterly MCR needs these on every mortgage
// loan/application; nullable = not yet answered, never defaulted.

export type LienStatus = "first_lien" | "subordinate_lien" | "not_secured_by_lien";
export const LIEN_STATUS_OPTIONS: ReadonlyArray<{ value: LienStatus; label: string }> = [
  { value: "first_lien", label: "First lien" },
  { value: "subordinate_lien", label: "Subordinate lien" },
  { value: "not_secured_by_lien", label: "Not secured by a lien" },
];

export type QmStatus = "qm" | "non_qm" | "not_subject";
export const QM_STATUS_OPTIONS: ReadonlyArray<{ value: QmStatus; label: string }> = [
  { value: "qm", label: "Qualified Mortgage (QM)" },
  { value: "non_qm", label: "Non-Qualified Mortgage" },
  { value: "not_subject", label: "Not subject to QM" },
];

// Application actions in the MCR's own vocabulary (AC030-AC070). A
// completed transaction derives closed_funded at report time; the
// explicit value classifies fell-through/cancelled applications.
export type NmlsActionTaken =
  | "closed_funded"
  | "approved_not_accepted"
  | "denied"
  | "withdrawn"
  | "file_closed_incomplete";
export const NMLS_ACTION_OPTIONS: ReadonlyArray<{ value: NmlsActionTaken; label: string }> = [
  { value: "closed_funded", label: "Closed and funded" },
  { value: "approved_not_accepted", label: "Approved but not accepted" },
  { value: "denied", label: "Denied" },
  { value: "withdrawn", label: "Withdrawn" },
  { value: "file_closed_incomplete", label: "File closed for incompleteness" },
];

// Drives PM comp override per CLAUDE.md "Mortgage (Private Money)" section.
export type PMSpecialStructure = "multifamily" | "commercial" | "sfr_over_3m" | "standard";

// The mortgage_loans projection the service-record detail page renders
// (Baker's 2026-07-13 bug: a PM loan's record page showed no loan data
// and nothing to edit). Deliberately NOT the full row: INTERNAL_ONLY
// free-text columns (notes, borrower_tier_notes, pm_comp_override_reason)
// and the sparse JSON blobs are excluded — the page has no business
// rendering them prominently, and leaving them out keeps the payload
// honest. Columns span 0006 (core), 0027 (CLR enrichment incl. the
// *_cents money columns + borrower identity), 0089/0201 (program type),
// 0136 (financials snapshot), 0197 (loan officer).
export interface MortgageLoanDetail {
  id: UUID;
  service_record_id: UUID;
  loan_type: LoanType;
  loan_product: string | null;
  loan_program_type: string | null;
  loan_program_type_note: string | null;
  lender_vendor_id: UUID | null;
  lender_name: string | null;
  loan_number: string | null;
  // The LENDER's own loan number (0027), distinct from ours — the one a
  // lender's AE or portal will ask for.
  lender_loan_number: string | null;
  // Economics — REAL dollars (0006) with cents-integer preferred where
  // present (0027). The UI falls back origination_amount ← loan_amount_cents.
  origination_amount: number | null;
  loan_amount_cents: number | null;
  purchase_price_cents: number | null;
  // Appraised value (CLR authoritative today; the future in-OS LOS will
  // write it at appraisal-received). Null until an appraisal lands.
  appraisal_value_cents: number | null;
  interest_rate: number | null;
  interest_rate_bps: number | null;
  /**
   * Where the rate came from (migration 20260911060137). NULL reads as
   * "recorded, origin not stated" — never as an estimate.
   */
  interest_rate_source: InterestRateSource | null;
  /**
   * 1 when interest_rate is the market average for the funding month,
   * not the note rate. Every surface shows "est." beside it; the Closed
   * Loan Report blanks it.
   */
  interest_rate_is_estimate: 0 | 1;
  rate_type: RateType | null;
  term_months: number | null;
  ltv_percent: number | null;
  // Fees Stewardship Mortgage earned (CLR authoritative; advisor-entered
  // for gaps — the field the pre-2018 PM revenue-gap cleanup item needs).
  total_amount_earned_cents: number | null;
  broker_fee_cents: number | null;
  voe_condo_other_fee_cents: number | null;
  client_savings_cents: number | null;
  // The loan amount at origination (CLR authoritative), distinct from
  // loan_amount_cents which may be updated post-close. Baker's field-
  // visibility ask, 2026-08-12: "Initial Loan Amount" alongside the
  // current/final loan amount.
  initial_loan_amount_cents: number | null;
  // Financials (Joey PR 4, 2026-08-27): advisor/CD-entered today, LOS-
  // written later. Down payment + totals are computed, never stored.
  cash_to_close_cents: number | null;
  monthly_pi_cents: number | null;
  monthly_taxes_cents: number | null;
  monthly_insurance_cents: number | null;
  monthly_mi_cents: number | null;
  monthly_hoa_cents: number | null;
  earnest_money_cents: number | null;
  seller_credit_cents: number | null;
  lender_credit_cents: number | null;
  // Dates. signing_date (wet-sign appointment) vs closing_date (COE) is
  // the distinction Joey's Processing redesign leads with — both shown
  // above the fold on an active file.
  application_date: ISODate | null;
  clear_to_close_date: ISODate | null;
  signing_date: ISODate | null;
  closing_date: ISODate | null;
  funded_date: ISODate | null;
  // The date the first mortgage payment is due — normally read off the
  // Note, not the Closing Disclosure (Cambria, 2026-09-02). Hand-typed,
  // same as signing_date; shown on her own loan-summary view only (see
  // web/src/settings/user-settings-registry.ts, PROC_LOAN_SUMMARY_CREDITS_SLOT).
  first_payment_date: ISODate | null;
  // Property
  property_address_line_1: string | null;
  property_address_line_2: string | null;
  property_city: string | null;
  property_state: string | null;
  property_zip: string | null;
  property_type: string | null;
  property_use: string | null;
  // Private Money
  borrower_tier: BorrowerTier | null;
  // NMLS MCR classifications (0249). Null = not yet answered.
  lien_status: LienStatus | null;
  hoepa_status: SqliteBoolean | null;
  qm_status: QmStatus | null;
  // Borrower identity (CLR authoritative; renders when household match
  // was fuzzy at migration time)
  borrower_full_name_raw: string | null;
  borrower_first_name: string | null;
  borrower_last_name: string | null;
  loan_officer_name: string | null;
  /** The Processing owner's name, offered only when loan_officer_name is
   *  blank — the same fallback the Closed Loan Report and Loan Log read.
   *  Never stored; computed alongside the loan on every read. */
  loan_officer_fallback_name: string | null;
  // Advisor-entered qualifying financials (0136)
  gross_monthly_income: number | null;
  dti_percent: number | null;
  financials_as_of: ISODate | null;
}

/**
 * The loan facts a Processing screen renders, resolved from whichever
 * home currently holds them (Joey Ellis's round-2 feedback, 2026-08-27).
 *
 * A mortgage file in Processing has no `mortgage_loans` row yet — the
 * loan is created at Funded, deliberately (Grant, 2026-08-18). Until
 * then the facts live on `mortgage_loan_applications`, keyed on the
 * Processing record. This is the single shape both cases resolve to, so
 * no screen has to ask which one it is looking at.
 *
 * `source` tells the UI where a save should go, and lets a card say
 * plainly that these are application figures rather than a funded
 * loan's.
 */
export interface MortgageLoanFacts {
  source: "loan" | "application";
  /** Set only when source === "loan" — the row a loan-details PATCH targets. */
  mortgage_loan_id: UUID | null;
  loan_type: LoanType | null;

  loan_amount_cents: number | null;
  purchase_price_cents: number | null;
  appraisal_value_cents: number | null;
  /**
   * Percent. COMPUTED from loan ÷ price whenever both are known, falling
   * back to the loan row's stored column for funded loans whose price
   * was never captured. Never stored on an application — a stored ratio
   * can drift from the two numbers it comes from.
   */
  ltv_percent: number | null;
  loan_program_type: string | null;
  loan_program_type_note: string | null;
  interest_rate: number | null;
  rate_type: RateType | null;
  term_months: number | null;

  signing_date: ISODate | null;
  closing_date: ISODate | null;
  funded_date: ISODate | null;
  // See the matching field on MortgageLoanDetail above — same source,
  // same per-viewer display rule.
  first_payment_date: ISODate | null;

  lender_name: string | null;
  loan_number: string | null;
  lender_loan_number: string | null;

  cash_to_close_cents: number | null;
  monthly_pi_cents: number | null;
  monthly_taxes_cents: number | null;
  monthly_insurance_cents: number | null;
  monthly_mi_cents: number | null;
  monthly_hoa_cents: number | null;
  earnest_money_cents: number | null;
  seller_credit_cents: number | null;
  lender_credit_cents: number | null;

  // Subject property — carried from the application (20260902203452)
  // until a loan exists, then from mortgage_loans, exactly like every
  // other fact above.
  property_address_line_1: string | null;
  property_address_line_2: string | null;
  property_city: string | null;
  property_state: string | null;
  property_zip: string | null;
  property_use: PropertyUse | null;
}

// Borrower-tier options for the PM tier select (matches the 0006 CHECK
// constraint exactly — a value outside this list fails the DB write).
export const BORROWER_TIER_OPTIONS: ReadonlyArray<{
  value: BorrowerTier;
  label: string;
}> = [
  { value: "a_plus", label: "A+" },
  { value: "a", label: "A" },
  { value: "a_minus", label: "A−" },
  { value: "b_plus", label: "B+" },
  { value: "b", label: "B" },
  { value: "b_minus", label: "B−" },
  { value: "c_plus", label: "C+" },
  { value: "c", label: "C" },
  { value: "c_minus", label: "C−" },
  { value: "d", label: "D" },
  { value: "other", label: "Other" },
];

export function isBorrowerTier(v: string): v is BorrowerTier {
  return BORROWER_TIER_OPTIONS.some((o) => o.value === v);
}

export interface MortgageLoanRow extends AuditedRow {
  service_record_id: UUID;

  loan_type: LoanType;
  loan_product: LoanProduct | null;

  lender_vendor_id: UUID | null;
  lender_name: string | null;

  loan_number: string | null;

  // Economics
  origination_amount: number | null;
  interest_rate: number | null;
  rate_type: RateType | null;
  term_months: number | null;
  ltv_percent: number | null;

  // Dates
  application_date: ISODate | null;
  clear_to_close_date: ISODate | null;
  funded_date: ISODate | null;
  payoff_date: ISODate | null;

  // Property
  property_address_line_1: string | null;
  property_address_line_2: string | null;
  property_city: string | null;
  property_state: string | null;
  property_zip: string | null;
  property_type: PropertyType | null;
  property_use: PropertyUse | null;

  // Cached servicing data
  current_balance: number | null;
  escrow_balance: number | null;
  next_payment_due: ISODate | null;
  last_synced_at: Timestamp | null;
  servicer_vendor_id: UUID | null;
  servicer_portal_url: string | null;

  // Private Money-specific
  borrower_tier: BorrowerTier | null;
  // NMLS MCR classifications (0249). Null = not yet answered.
  lien_status: LienStatus | null;
  hoepa_status: SqliteBoolean | null;
  qm_status: QmStatus | null;
  // INTERNAL_ONLY.
  borrower_tier_notes: string | null;
  cross_collateral: SqliteBoolean;
  pm_special_structure: PMSpecialStructure | null;
  // INTERNAL_ONLY.
  pm_comp_override_reason: string | null;

  // Traditional EPO tracking
  epo_window_ends: ISODate | null;

  // Borrower qualifying financials (migration 0136). Advisor-entered
  // cached reference — the LOS stays authoritative. Income is MONTHLY
  // gross (the figure DTI is computed against). `financials_as_of` dates
  // the snapshot for staleness display.
  gross_monthly_income: number | null;
  dti_percent: number | null;
  financials_as_of: ISODate | null;
  financials_updated_by_user_id: UUID | null;

  // INTERNAL_ONLY.
  notes: string | null;
}
